import { Navigate, useNavigate } from 'react-router-dom';
import {
  Clock, ShieldCheck, XCircle, AlertTriangle, FileText, ArrowRight, LogOut, Loader2,
} from 'lucide-react';
import { useVendorStatus, type KycStatus } from '@/hooks/useVendorStatus';
import { useVendorStore } from '@/store/vendor-store';
import { Button, Card, CardContent } from '@nepal-ecom/ui';

const DOC_LABELS: Record<string, string> = {
  panCard: 'PAN / VAT certificate',
  pan: 'PAN / VAT certificate',
  citizenshipFront: 'Citizenship — front',
  citizenshipBack: 'Citizenship — back',
  businessRegistration: 'Business registration',
};

const META: Record<Exclude<KycStatus, 'approved'>, {
  icon: typeof Clock; title: string; body: string; tint: string; iconColor: string;
}> = {
  pending: {
    icon: Clock,
    title: 'Finish your KYC to start selling',
    body: 'Your store has been created. Upload your KYC documents and submit them for review — our team verifies new stores within 1–2 business days.',
    tint: 'bg-amber-50 ring-amber-200',
    iconColor: 'text-amber-600',
  },
  under_review: {
    icon: Clock,
    title: 'Your application is under review',
    body: "Thanks for submitting your documents. Our team is reviewing them now — you'll get an email the moment your store is approved. This usually takes 1–2 business days.",
    tint: 'bg-cobalt-50 ring-cobalt-200',
    iconColor: 'text-cobalt-600',
  },
  rejected: {
    icon: XCircle,
    title: 'Your application needs changes',
    body: 'A few documents were not accepted. Review the notes below, re-upload only the documents that need fixing, and resubmit — you do not need to start over.',
    tint: 'bg-red-50 ring-red-200',
    iconColor: 'text-red-600',
  },
  suspended: {
    icon: AlertTriangle,
    title: 'Your store is suspended',
    body: 'Your store access has been suspended. Please contact support to resolve this and restore your account.',
    tint: 'bg-slate-100 ring-slate-200',
    iconColor: 'text-slate-600',
  },
};

export function PendingApproval() {
  const navigate = useNavigate();
  const logout = useVendorStore((s) => s.logout);
  const { status, rejectionReason, documentReviews, isLoading } = useVendorStatus();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen text-slate-500">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading your store…
      </div>
    );
  }

  // An approved vendor should never see this screen — bounce to the dashboard.
  if (status === 'approved') return <Navigate to="/" replace />;

  const meta = META[status];
  const Icon = meta.icon;
  const rejectedDocs = Object.entries(documentReviews).filter(([, r]) => r?.status === 'rejected');

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-xl space-y-5">
        <Card className={`border-0 ring-1 ${meta.tint}`}>
          <CardContent className="p-7">
            <div className="flex items-start gap-4">
              <div className="h-14 w-14 rounded-2xl bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                <Icon className={`h-7 w-7 ${meta.iconColor}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  {status.replace('_', ' ')}
                </p>
                <h1 className="text-xl font-bold text-slate-900 mt-1">{meta.title}</h1>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{meta.body}</p>
              </div>
            </div>

            {status === 'rejected' && (rejectionReason || rejectedDocs.length > 0) && (
              <div className="mt-5 rounded-xl bg-white ring-1 ring-red-200 p-4 space-y-3">
                {rejectionReason && (
                  <div>
                    <p className="text-xs font-bold text-red-700 uppercase tracking-wider">Reviewer note</p>
                    <p className="text-sm text-slate-700 mt-1">{rejectionReason}</p>
                  </div>
                )}
                {rejectedDocs.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-red-700 uppercase tracking-wider">Documents to re-upload</p>
                    <ul className="mt-2 space-y-1.5">
                      {rejectedDocs.map(([key, r]) => (
                        <li key={key} className="flex items-start gap-2 text-sm text-slate-700">
                          <FileText className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                          <span>
                            <span className="font-semibold">{DOC_LABELS[key] ?? key}</span>
                            {r?.note && <span className="text-slate-500"> — {r.note}</span>}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-2">
              {(status === 'pending' || status === 'rejected') && (
                <Button onClick={() => navigate('/store')}>
                  {status === 'rejected' ? 'Update documents & resubmit' : 'Complete KYC'}
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              )}
              {status === 'under_review' && (
                <Button variant="outline" onClick={() => navigate('/store')}>
                  Review my submission
                </Button>
              )}
              <Button variant="ghost" onClick={() => { logout(); navigate('/login', { replace: true }); }}>
                <LogOut className="h-4 w-4 mr-1" /> Log out
              </Button>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-400 flex items-center justify-center gap-1.5">
          <ShieldCheck className="h-3.5 w-3.5" />
          Verified stores get the green badge, priority placement, and weekly payouts.
        </p>
      </div>
    </div>
  );
}
